/**
 * Map Editor Tab Content for GameMenu
 * In-game map editor with tile painting, prop placement, spawn areas
 * Supports full zone configuration for act maps
 */

import { MenuTabContent } from '../MenuTabContent';
import { Player } from '@client/entities/Player';
import { PropType, PropRegistry } from '@client/assets/props';
import { TileType } from '@client/world/MapTemplates';
import { getSaveManager, SavedMapData, MapListInfo } from '@client/save/SaveManager';
import { ZoneType, EnemySpawnType, EnemySpawnConfig } from '@shared/world/Zone';

// Editor tools
export type EditorTool =
  | 'tile_brush'      // Paint tile types
  | 'spawn_area'      // Mark enemy spawn areas
  | 'walkable_area'   // Mark walkable areas
  | 'prop'            // Place props
  | 'npc'             // Place NPCs
  | 'waypoint'        // Place waypoints
  | 'portal'          // Place portals
  | 'eraser'          // Remove props/spawns
  | 'select';         // Select and move

// Spawn area types
export type SpawnAreaType = 'enemy' | 'player' | 'boss';

// NPC types
export type NPCType = 'merchant' | 'quest_giver' | 'blacksmith' | 'healer' | 'guard' | 'villager';

// Waypoint functions
export type WaypointFunction = 'checkpoint' | 'teleport' | 'quest_marker' | 'spawn_point' | 'trigger';

// Zone configuration for act maps
export interface EditorZoneConfig {
  zoneType: ZoneType;
  zoneName: string;
  description: string;
  act: 1 | 2 | 3 | 4;
  levelRange: { min: number; max: number };
  isSafeZone: boolean;
  // Visual
  tilesetId: string;
  ambientColor: string;
  fogColor?: string;
  fogDensity?: number;
  // Audio
  musicTheme?: string;
  ambientSounds?: string[];
  // Spawning
  maxEnemies: number;
  spawnInterval: number;
  // Boss
  bossType?: EnemySpawnType;
}

// Editor state that can be saved/loaded
export interface EditorMapData {
  width: number;
  height: number;
  tiles: number[][];           // TileType values
  props: EditorProp[];
  spawnAreas: EditorSpawnArea[];
  waypoints: EditorWaypoint[];
  portals: EditorPortal[];
  npcs: EditorNPC[];
  // Zone configuration
  zoneConfig?: EditorZoneConfig;
}

export interface EditorProp {
  type: PropType;
  x: number;
  y: number;
  variant?: number;
}

export interface EditorSpawnArea {
  type: SpawnAreaType;
  x: number;
  y: number;
  radius: number;
  difficulty?: number;  // 1-10, affects enemy spawn strength
  // Enemy spawn configuration for this area
  enemySpawns?: EnemySpawnConfig[];
}

export interface EditorWaypoint {
  id: string;
  x: number;
  y: number;
  name: string;
  function: WaypointFunction;
  targetId?: string;  // For teleport - target waypoint id
}

export interface EditorPortal {
  x: number;
  y: number;
  targetZone: ZoneType;
  portalType: 'path' | 'portal' | 'gate';
  requiresLevel?: number;
  requiresItem?: string;
}

export interface EditorNPC {
  id: string;
  x: number;
  y: number;
  type: NPCType;
  name: string;
  dialogue?: string;
}

// Tile info for palette
interface TileInfo {
  type: TileType;
  name: string;
  color: string;
  walkable: boolean;
}

const TILE_PALETTE: TileInfo[] = [
  { type: TileType.GRASS, name: 'Cim', color: '#4a7c3f', walkable: true },
  { type: TileType.DIRT, name: 'Toprak', color: '#8b7355', walkable: true },
  { type: TileType.STONE, name: 'Tas', color: '#6b6b6b', walkable: true },
  { type: TileType.WATER, name: 'Su', color: '#3d6b8a', walkable: false },
  { type: TileType.SAND, name: 'Kum', color: '#c9b896', walkable: true },
  { type: TileType.DARK_GRASS, name: 'Koyu Cim', color: '#2a4a2a', walkable: true },
  { type: TileType.MUD, name: 'Camur', color: '#4a3a2a', walkable: true },
  { type: TileType.SNOW, name: 'Kar', color: '#e8e8f0', walkable: true },
  { type: TileType.LAVA, name: 'Lav', color: '#ff4400', walkable: false },
  { type: TileType.VOID, name: 'Bosluk', color: '#1a1a2a', walkable: false },
];

// Prop categories for organization
interface PropCategory {
  name: string;
  props: PropType[];
}

const PROP_CATEGORIES: PropCategory[] = [
  {
    name: 'Duvarlar',
    props: ['wall', 'wall_corner', 'fence', 'fence_corner', 'gate', 'wall_gate']
  },
  {
    name: 'Yukselti',
    props: ['elevation', 'cliff', 'stairs', 'stairs_down', 'ramp', 'bridge', 'pillar']
  },
  {
    name: 'Agaclar',
    props: ['tree_pine', 'tree_oak', 'tree_dead', 'bush']
  },
  {
    name: 'Yapilar',
    props: ['house', 'tent', 'well', 'rock']
  },
  {
    name: 'Nesneler',
    props: ['barrel', 'crate', 'cart', 'torch', 'sign', 'campfire']
  },
  {
    name: 'Deko',
    props: ['grave', 'ruins', 'statue']
  },
];

// Tool info
interface ToolInfo {
  id: EditorTool;
  name: string;
  icon: string;
  description: string;
}

const TOOLS: ToolInfo[] = [
  { id: 'tile_brush', name: 'Zemin', icon: '🖌️', description: 'Zemin boyama' },
  { id: 'spawn_area', name: 'Spawn', icon: '👹', description: 'Dusman dogma alani' },
  { id: 'prop', name: 'Nesne', icon: '🌲', description: 'Nesne yerlestir' },
  { id: 'npc', name: 'NPC', icon: '🧑', description: 'NPC yerlestir' },
  { id: 'waypoint', name: 'Waypoint', icon: '📍', description: 'Waypoint ekle' },
  { id: 'portal', name: 'Portal', icon: '🌀', description: 'Portal ekle' },
  { id: 'eraser', name: 'Silgi', icon: '🧹', description: 'Sil' },
  { id: 'select', name: 'Sec', icon: '👆', description: 'Sec ve duzenle' },
];

// NPC type info
const NPC_TYPES: { type: NPCType; name: string; icon: string }[] = [
  { type: 'merchant', name: 'Tüccar', icon: '💰' },
  { type: 'quest_giver', name: 'Görev Veren', icon: '❗' },
  { type: 'blacksmith', name: 'Demirci', icon: '⚒️' },
  { type: 'healer', name: 'Şifacı', icon: '💚' },
  { type: 'guard', name: 'Muhafız', icon: '🛡️' },
  { type: 'villager', name: 'Köylü', icon: '👤' },
];

// Waypoint function info
const WAYPOINT_FUNCTIONS: { func: WaypointFunction; name: string; icon: string }[] = [
  { func: 'checkpoint', name: 'Checkpoint', icon: '🚩' },
  { func: 'teleport', name: 'Teleport', icon: '✨' },
  { func: 'quest_marker', name: 'Görev İşareti', icon: '❓' },
  { func: 'spawn_point', name: 'Doğum Noktası', icon: '🏠' },
  { func: 'trigger', name: 'Tetikleyici', icon: '⚡' },
];

// Zone types by act
const ZONE_TYPES: { zone: ZoneType; name: string; act: number }[] = [
  // Act 1
  { zone: 'otag_koyu', name: 'Otağ Köyü', act: 1 },
  { zone: 'karanlik_orman', name: 'Karanlık Orman', act: 1 },
  { zone: 'ruh_batakligi', name: 'Ruh Bataklığı', act: 1 },
  { zone: 'erlik_tapinagi', name: 'Erlik Tapınağı', act: 1 },
  // Act 2
  { zone: 'ergenekon_girisi', name: 'Ergenekon Girişi', act: 2 },
  { zone: 'firtina_ovasi', name: 'Fırtına Ovası', act: 2 },
  { zone: 'ates_magarasi', name: 'Ateş Mağarası', act: 2 },
  { zone: 'yersub_tahti', name: 'Yer-Sub Tahtı', act: 2 },
  // Act 3
  { zone: 'karanlik_tunel', name: 'Karanlık Tünel', act: 3 },
  { zone: 'olum_nehri', name: 'Ölüm Nehri', act: 3 },
  { zone: 'kemik_ormani', name: 'Kemik Ormanı', act: 3 },
  { zone: 'lanetli_saray', name: 'Lanetli Saray', act: 3 },
  // Act 4
  { zone: 'erlik_kapisi', name: 'Erlik Kapısı', act: 4 },
  { zone: 'cehennem_bahcesi', name: 'Cehennem Bahçesi', act: 4 },
  { zone: 'taht_odasi', name: 'Taht Odası', act: 4 },
];

// Enemy types by act
const ENEMY_TYPES: { type: EnemySpawnType; name: string; act: number }[] = [
  // Act 1
  { type: 'kormos', name: 'Körmös', act: 1 },
  { type: 'al_kormos', name: 'Al Körmös', act: 1 },
  { type: 'karabasan', name: 'Karabasan', act: 1 },
  { type: 'albasti', name: 'Albastı', act: 1 },
  { type: 'karakura', name: 'Karakura', act: 1 },
  { type: 'sulbus', name: 'Sülbüs', act: 1 },
  { type: 'erlik_okcusu', name: 'Erlik Okçusu', act: 1 },
  { type: 'erlik_guard', name: 'Erlik Muhafızı', act: 1 },
  { type: 'albasti_kralicesi', name: 'Albastı Kraliçesi (Boss)', act: 1 },
  // Act 2
  { type: 'demir_kurt', name: 'Demir Kurt', act: 2 },
  { type: 'dag_devi', name: 'Dağ Devi', act: 2 },
  { type: 'ruzgar_ruhu', name: 'Rüzgar Ruhu', act: 2 },
  { type: 'ates_iblisi', name: 'Ateş İblisi', act: 2 },
  { type: 'kaya_golemi', name: 'Kaya Golemi', act: 2 },
  { type: 'yer_sub', name: 'Yer-Sub (Boss)', act: 2 },
  // Act 3
  { type: 'golge_avcisi', name: 'Gölge Avcısı', act: 3 },
  { type: 'kemik_savasci', name: 'Kemik Savaşçısı', act: 3 },
  { type: 'lanetli_ruh', name: 'Lanetli Ruh', act: 3 },
  { type: 'olum_habercisi', name: 'Ölüm Habercisi', act: 3 },
  { type: 'erlik_elcisi', name: 'Erlik Elçisi (Boss)', act: 3 },
  // Act 4
  { type: 'erlik_sovalyesi', name: 'Erlik Şövalyesi', act: 4 },
  { type: 'karanlik_seraf', name: 'Karanlık Seraf', act: 4 },
  { type: 'erlik_han', name: 'Erlik Han (Final Boss)', act: 4 },
];

// Portal types
const PORTAL_TYPES: { type: 'path' | 'portal' | 'gate'; name: string; icon: string }[] = [
  { type: 'path', name: 'Yol', icon: '🚶' },
  { type: 'portal', name: 'Portal', icon: '🌀' },
  { type: 'gate', name: 'Kapı', icon: '🚪' },
];

// Tileset IDs map to zone visual themes
const TILESET_MAP: Record<ZoneType, string> = {
  'otag_koyu': 'village',
  'karanlik_orman': 'dark_forest',
  'ruh_batakligi': 'swamp',
  'erlik_tapinagi': 'temple',
  'ergenekon_girisi': 'mountain_entrance',
  'firtina_ovasi': 'steppe',
  'ates_magarasi': 'fire_cave',
  'yersub_tahti': 'underground_palace',
  'karanlik_tunel': 'dark_tunnel',
  'olum_nehri': 'death_river',
  'kemik_ormani': 'bone_forest',
  'lanetli_saray': 'cursed_palace',
  'erlik_kapisi': 'hell_gate',
  'cehennem_bahcesi': 'hell_garden',
  'taht_odasi': 'throne_room',
};

export class EditorTab extends MenuTabContent {
  // Current tool state
  private currentTool: EditorTool = 'tile_brush';
  private selectedTileType: TileType = TileType.GRASS;
  private selectedPropType: PropType = 'tree_oak';
  private selectedSpawnType: SpawnAreaType = 'enemy';
  private selectedNPCType: NPCType = 'merchant';
  private selectedWaypointFunc: WaypointFunction = 'checkpoint';
  private selectedPortalType: 'path' | 'portal' | 'gate' = 'portal';
  private selectedTargetZone: ZoneType = 'otag_koyu';
  private portalRequiredLevel: number = 1;
  private brushSize: number = 1;
  private propVariant: number = 0;
  private spawnDifficulty: number = 1;

  // Zone configuration state
  private showZoneConfig: boolean = false;

  // Selection state for editing properties
  private selectedElement: {
    type: 'prop' | 'npc' | 'waypoint' | 'portal' | 'spawn';
    index: number;
  } | null = null;

  // UI state
  private hoveredTool: EditorTool | null = null;
  private hoveredTile: TileType | null = null;
  private hoveredProp: PropType | null = null;
  private propCategoryIndex: number = 0;
  private showGrid: boolean = true;
  private showSpawnAreas: boolean = true;

  // Expand map visual state
  private hoveredExpandDir: 'north' | 'south' | 'east' | 'west' | null = null;

  // Map data
  private mapData: EditorMapData;

  // Scroll state for panels (reserved for future use)
  // private propScrollY: number = 0;

  // Map view state
  private mapOffsetX: number = 0;
  private mapOffsetY: number = 0;
  private mapZoom: number = 0.5;
  private isDraggingMap: boolean = false;
  private lastMouseX: number = 0;
  private lastMouseY: number = 0;

  // Callbacks
  public onMapDataChange?: (data: EditorMapData) => void;
  public onSaveMap?: (data: EditorMapData, name: string) => void;
  public onLoadMap?: () => EditorMapData | null;

  constructor(player: Player) {
    super(player);

    // Initialize with empty map
    this.mapData = this.createEmptyMap(40, 30);
  }

  private createEmptyMap(width: number, height: number): EditorMapData {
    const tiles: number[][] = [];
    for (let y = 0; y < height; y++) {
      tiles[y] = [];
      for (let x = 0; x < width; x++) {
        tiles[y][x] = TileType.GRASS;
      }
    }

    return {
      width,
      height,
      tiles,
      props: [],
      spawnAreas: [],
      waypoints: [],
      portals: [],
      npcs: [],
      zoneConfig: {
        zoneType: 'karanlik_orman',
        zoneName: 'Yeni Zone',
        description: '',
        act: 1,
        levelRange: { min: 1, max: 10 },
        isSafeZone: false,
        tilesetId: TILESET_MAP['karanlik_orman'],
        ambientColor: '#1a2f1a',
        maxEnemies: 12,
        spawnInterval: 4,
      },
    };
  }

  measureSize(_ctx: CanvasRenderingContext2D): { width: number; height: number } {
    return { width: 1000, height: 600 };
  }

  update(_deltaTime: number): void {}

  onActivate(): void {
    this.hoveredTool = null;
    this.hoveredTile = null;
    this.hoveredProp = null;
  }

  // Layout calculations
  private getLayout() {
    const { width, height } = this.bounds;
    const toolbarWidth = 60;
    const leftPanelWidth = 180;
    const rightPanelWidth = 200;
    const topBarHeight = 40;
    const bottomBarHeight = 30;

    return {
      // Toolbar (left edge)
      toolbar: {
        x: this.bounds.x,
        y: this.bounds.y + topBarHeight,
        width: toolbarWidth,
        height: height - topBarHeight - bottomBarHeight,
      },
      // Left panel (tile/prop palette)
      leftPanel: {
        x: this.bounds.x + toolbarWidth,
        y: this.bounds.y + topBarHeight,
        width: leftPanelWidth,
        height: height - topBarHeight - bottomBarHeight,
      },
      // Map view (center)
      mapView: {
        x: this.bounds.x + toolbarWidth + leftPanelWidth,
        y: this.bounds.y + topBarHeight,
        width: width - toolbarWidth - leftPanelWidth - rightPanelWidth,
        height: height - topBarHeight - bottomBarHeight,
      },
      // Right panel (properties)
      rightPanel: {
        x: this.bounds.x + width - rightPanelWidth,
        y: this.bounds.y + topBarHeight,
        width: rightPanelWidth,
        height: height - topBarHeight - bottomBarHeight,
      },
      // Top bar
      topBar: {
        x: this.bounds.x,
        y: this.bounds.y,
        width: width,
        height: topBarHeight,
      },
      // Bottom bar
      bottomBar: {
        x: this.bounds.x,
        y: this.bounds.y + height - bottomBarHeight,
        width: width,
        height: bottomBarHeight,
      },
      fontSize: 12,
    };
  }

  protected renderContent(ctx: CanvasRenderingContext2D): void {
    const layout = this.getLayout();

    // Render each section
    this.renderTopBar(ctx, layout);
    this.renderToolbar(ctx, layout);
    this.renderLeftPanel(ctx, layout);
    this.renderMapView(ctx, layout);
    this.renderRightPanel(ctx, layout);
    this.renderBottomBar(ctx, layout);

    // Render load dialog on top if open
    if (this.showLoadDialog) {
      this.renderLoadDialog(ctx);
    }
  }

  private renderLoadDialog(ctx: CanvasRenderingContext2D): void {
    const { x, y, width, height } = this.bounds;

    // Darkened overlay
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    ctx.fillRect(x, y, width, height);

    // Dialog box
    const dialogW = 400;
    const dialogH = 350;
    const dialogX = x + (width - dialogW) / 2;
    const dialogY = y + (height - dialogH) / 2;

    // Dialog background
    ctx.fillStyle = '#1a1510';
    this.drawRoundedRect(ctx, dialogX, dialogY, dialogW, dialogH, 8);
    ctx.fill();

    ctx.strokeStyle = '#d4af37';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Title
    ctx.fillStyle = '#d4af37';
    ctx.font = 'bold 16px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('HARITA YUKLE', dialogX + dialogW / 2, dialogY + 30);

    // Close button
    ctx.fillStyle = '#ff4444';
    ctx.font = 'bold 16px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('✕', dialogX + dialogW - 20, dialogY + 25);

    // Saved maps list
    const listX = dialogX + 20;
    const listY = dialogY + 50;
    const listW = dialogW - 40;
    const listH = dialogH - 100;
    const itemH = 50;

    // List background
    ctx.fillStyle = '#0d0a08';
    this.drawRoundedRect(ctx, listX, listY, listW, listH, 4);
    ctx.fill();

    // Clip to list area
    ctx.save();
    ctx.beginPath();
    ctx.rect(listX, listY, listW, listH);
    ctx.clip();

    if (this.savedMapsList.length === 0) {
      // No saved maps
      ctx.fillStyle = '#606060';
      ctx.font = '14px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('Kayitli harita yok', listX + listW / 2, listY + listH / 2);
    } else {
      // Render each map item
      for (let i = 0; i < this.savedMapsList.length; i++) {
        const map = this.savedMapsList[i];
        const itemY = listY + 5 + i * itemH - this.loadDialogScrollY;

        // Skip if outside visible area
        if (itemY + itemH < listY || itemY > listY + listH) continue;

        const isHovered = this.hoveredMapIndex === i;

        // Item background
        if (isHovered) {
          ctx.fillStyle = '#2a2520';
          this.drawRoundedRect(ctx, listX + 5, itemY, listW - 10, itemH - 5, 4);
          ctx.fill();
        }

        // Map name
        ctx.fillStyle = isHovered ? '#d4af37' : '#c0c0c0';
        ctx.font = 'bold 13px Arial';
        ctx.textAlign = 'left';
        ctx.fillText(map.name, listX + 15, itemY + 20);

        // Map info
        ctx.fillStyle = '#808080';
        ctx.font = '11px Arial';
        ctx.fillText(`${map.width}x${map.height} - ${map.biome}`, listX + 15, itemY + 36);

        // Date
        const date = new Date(map.timestamp);
        const dateStr = `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
        ctx.fillStyle = '#606060';
        ctx.font = '10px Arial';
        ctx.textAlign = 'right';
        ctx.fillText(dateStr, listX + listW - 15, itemY + 28);
      }
    }

    ctx.restore();

    // Scrollbar if needed
    const totalHeight = this.savedMapsList.length * itemH;
    if (totalHeight > listH) {
      const scrollbarH = (listH / totalHeight) * listH;
      const scrollbarY = listY + (this.loadDialogScrollY / totalHeight) * listH;

      ctx.fillStyle = '#3d3228';
      this.drawRoundedRect(ctx, listX + listW - 8, scrollbarY, 6, scrollbarH, 3);
      ctx.fill();
    }

    // Cancel button
    const btnW = 100;
    const btnH = 30;
    const cancelX = dialogX + dialogW / 2 - btnW / 2;
    const cancelY = dialogY + dialogH - 45;

    ctx.fillStyle = '#2a2520';
    this.drawRoundedRect(ctx, cancelX, cancelY, btnW, btnH, 4);
    ctx.fill();

    ctx.strokeStyle = '#606060';
    ctx.lineWidth = 1;
    ctx.stroke();

    ctx.fillStyle = '#c0c0c0';
    ctx.font = 'bold 12px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('Iptal', cancelX + btnW / 2, cancelY + btnH / 2 + 4);
  }

  private renderTopBar(ctx: CanvasRenderingContext2D, layout: ReturnType<typeof this.getLayout>): void {
    const { x, y, width, height } = layout.topBar;

    // Background
    ctx.fillStyle = '#1a1510';
    ctx.fillRect(x, y, width, height);

    // Title
    ctx.fillStyle = '#d4af37';
    ctx.font = 'bold 16px Arial';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText('HARITA EDITORU', x + 15, y + height / 2);

    // Current map name
    ctx.fillStyle = '#c0c0c0';
    ctx.font = '13px Arial';
    ctx.fillText(`- ${this.currentMapName}`, x + 175, y + height / 2);

    // Map dimensions
    ctx.fillStyle = '#606060';
    ctx.font = '11px Arial';
    ctx.fillText(`(${this.mapData.width}x${this.mapData.height})`, x + 175 + ctx.measureText(`- ${this.currentMapName}`).width + 10, y + height / 2);

    // Buttons (right side)
    const buttonY = y + 6;
    const buttonH = height - 12;
    let buttonX = x + width - 15;

    // Save button
    buttonX -= 70;
    this.drawButton(ctx, buttonX, buttonY, 65, buttonH, 'Kaydet', '#1a4a1a', '#2ecc71');

    // New button
    buttonX -= 55;
    this.drawButton(ctx, buttonX, buttonY, 50, buttonH, 'Yeni', '#2a2520', '#808080');

    // Load button
    buttonX -= 55;
    this.drawButton(ctx, buttonX, buttonY, 50, buttonH, 'Yukle', '#2a2520', '#808080');
  }

  private renderToolbar(ctx: CanvasRenderingContext2D, layout: ReturnType<typeof this.getLayout>): void {
    const { x, y, width, height } = layout.toolbar;

    // Background
    ctx.fillStyle = '#151210';
    ctx.fillRect(x, y, width, height);

    // Border
    ctx.strokeStyle = '#3d3228';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x + width, y);
    ctx.lineTo(x + width, y + height);
    ctx.stroke();

    // Tools
    const toolSize = 44;
    const padding = 8;
    let toolY = y + padding;

    for (const tool of TOOLS) {
      const isSelected = this.currentTool === tool.id;
      const isHovered = this.hoveredTool === tool.id;

      // Tool button background
      if (isSelected) {
        ctx.fillStyle = '#3d3228';
      } else if (isHovered) {
        ctx.fillStyle = '#2a2520';
      } else {
        ctx.fillStyle = 'transparent';
      }

      this.drawRoundedRect(ctx, x + padding, toolY, toolSize, toolSize, 5);
      ctx.fill();

      // Selection indicator
      if (isSelected) {
        ctx.strokeStyle = '#d4af37';
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      // Icon
      ctx.font = '20px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = isSelected ? '#d4af37' : '#808080';
      ctx.fillText(tool.icon, x + padding + toolSize / 2, toolY + toolSize / 2);

      toolY += toolSize + 4;
    }
  }

  private renderLeftPanel(ctx: CanvasRenderingContext2D, layout: ReturnType<typeof this.getLayout>): void {
    const { x, y, width, height } = layout.leftPanel;

    // Background
    ctx.fillStyle = '#0d0a08';
    ctx.fillRect(x, y, width, height);

    // Border
    ctx.strokeStyle = '#3d3228';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x + width, y);
    ctx.lineTo(x + width, y + height);
    ctx.stroke();

    // Content depends on current tool
    if (this.currentTool === 'tile_brush') {
      this.renderTilePalette(ctx, x + 10, y + 10, width - 20);
    } else if (this.currentTool === 'prop') {
      this.renderPropPalette(ctx, x + 10, y + 10, width - 20, height - 20);
    } else if (this.currentTool === 'spawn_area') {
      this.renderSpawnSettings(ctx, x + 10, y + 10, width - 20, height - 20);
    } else if (this.currentTool === 'npc') {
      this.renderNPCSettings(ctx, x + 10, y + 10, width - 20);
    } else if (this.currentTool === 'waypoint') {
      this.renderWaypointSettings(ctx, x + 10, y + 10, width - 20);
    } else if (this.currentTool === 'portal') {
      this.renderPortalSettings(ctx, x + 10, y + 10, width - 20, height - 20);
    } else if (this.currentTool === 'select') {
      this.renderSelectionInfo(ctx, x + 10, y + 10, width - 20, height - 20);
    } else {
      // Default info
      ctx.fillStyle = '#808080';
      ctx.font = '12px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('Arac secin', x + width / 2, y + height / 2);
    }
  }

  private renderNPCSettings(ctx: CanvasRenderingContext2D, x: number, y: number, width: number): void {
    // Title
    ctx.fillStyle = '#d4af37';
    ctx.font = 'bold 12px Arial';
    ctx.textAlign = 'left';
    ctx.fillText('NPC TÜRÜ', x, y + 12);

    // NPC type buttons
    const btnH = 36;
    const gap = 6;
    let btnY = y + 30;

    for (const npc of NPC_TYPES) {
      const isSelected = this.selectedNPCType === npc.type;

      ctx.fillStyle = isSelected ? '#3d3228' : '#1a1510';
      this.drawRoundedRect(ctx, x, btnY, width, btnH, 4);
      ctx.fill();

      if (isSelected) {
        ctx.strokeStyle = '#d4af37';
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      // Icon
      ctx.font = '16px Arial';
      ctx.textAlign = 'left';
      ctx.fillStyle = '#ffffff';
      ctx.fillText(npc.icon, x + 10, btnY + btnH / 2 + 5);

      // Name
      ctx.fillStyle = isSelected ? '#d4af37' : '#c0c0c0';
      ctx.font = '12px Arial';
      ctx.fillText(npc.name, x + 35, btnY + btnH / 2 + 4);

      btnY += btnH + gap;
    }
  }

  private renderWaypointSettings(ctx: CanvasRenderingContext2D, x: number, y: number, width: number): void {
    // Title
    ctx.fillStyle = '#d4af37';
    ctx.font = 'bold 12px Arial';
    ctx.textAlign = 'left';
    ctx.fillText('WAYPOINT İŞLEVİ', x, y + 12);

    // Waypoint function buttons
    const btnH = 36;
    const gap = 6;
    let btnY = y + 30;

    for (const wp of WAYPOINT_FUNCTIONS) {
      const isSelected = this.selectedWaypointFunc === wp.func;

      ctx.fillStyle = isSelected ? '#3d3228' : '#1a1510';
      this.drawRoundedRect(ctx, x, btnY, width, btnH, 4);
      ctx.fill();

      if (isSelected) {
        ctx.strokeStyle = '#4488ff';
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      // Icon
      ctx.font = '16px Arial';
      ctx.textAlign = 'left';
      ctx.fillStyle = '#ffffff';
      ctx.fillText(wp.icon, x + 10, btnY + btnH / 2 + 5);

      // Name
      ctx.fillStyle = isSelected ? '#4488ff' : '#c0c0c0';
      ctx.font = '12px Arial';
      ctx.fillText(wp.name, x + 35, btnY + btnH / 2 + 4);

      btnY += btnH + gap;
    }
  }

  private renderSelectionInfo(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, _height: number): void {
    // Title
    ctx.fillStyle = '#d4af37';
    ctx.font = 'bold 12px Arial';
    ctx.textAlign = 'left';
    ctx.fillText('SEÇİLİ ÖĞGE', x, y + 12);

    if (!this.selectedElement) {
      ctx.fillStyle = '#606060';
      ctx.font = '11px Arial';
      ctx.fillText('Haritada bir öğe seçin', x, y + 40);
      ctx.fillText('(NPC, Waypoint, Prop)', x, y + 56);
      return;
    }

    const { type, index } = this.selectedElement;
    let infoY = y + 35;

    ctx.fillStyle = '#c0c0c0';
    ctx.font = '11px Arial';

    if (type === 'npc' && this.mapData.npcs[index]) {
      const npc = this.mapData.npcs[index];
      const npcInfo = NPC_TYPES.find(n => n.type === npc.type);

      ctx.fillText(`Tür: ${npcInfo?.icon} ${npcInfo?.name}`, x, infoY);
      infoY += 18;
      ctx.fillText(`İsim: ${npc.name}`, x, infoY);
      infoY += 18;
      ctx.fillText(`Konum: (${npc.x}, ${npc.y})`, x, infoY);

    } else if (type === 'waypoint' && this.mapData.waypoints[index]) {
      const wp = this.mapData.waypoints[index];
      const wpInfo = WAYPOINT_FUNCTIONS.find(w => w.func === wp.function);

      ctx.fillText(`İşlev: ${wpInfo?.icon} ${wpInfo?.name}`, x, infoY);
      infoY += 18;
      ctx.fillText(`İsim: ${wp.name}`, x, infoY);
      infoY += 18;
      ctx.fillText(`Konum: (${wp.x}, ${wp.y})`, x, infoY);

    } else if (type === 'prop' && this.mapData.props[index]) {
      const prop = this.mapData.props[index];
      ctx.fillText(`Tür: ${prop.type}`, x, infoY);
      infoY += 18;
      ctx.fillText(`Konum: (${prop.x}, ${prop.y})`, x, infoY);

    } else if (type === 'spawn' && this.mapData.spawnAreas[index]) {
      const spawn = this.mapData.spawnAreas[index];
      ctx.fillText(`Tür: ${spawn.type}`, x, infoY);
      infoY += 18;
      ctx.fillText(`Zorluk: ${spawn.difficulty || 1}`, x, infoY);
      infoY += 18;
      ctx.fillText(`Konum: (${spawn.x}, ${spawn.y})`, x, infoY);
    }

    // Delete button
    infoY += 30;
    ctx.fillStyle = '#4a1a1a';
    this.drawRoundedRect(ctx, x, infoY, width, 28, 4);
    ctx.fill();
    ctx.strokeStyle = '#ff4444';
    ctx.lineWidth = 1;
    ctx.stroke();

    ctx.fillStyle = '#ff6666';
    ctx.font = 'bold 11px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('Sil', x + width / 2, infoY + 18);
    ctx.textAlign = 'left';
  }

  private renderTilePalette(ctx: CanvasRenderingContext2D, x: number, y: number, width: number): void {
    // Title
    ctx.fillStyle = '#d4af37';
    ctx.font = 'bold 12px Arial';
    ctx.textAlign = 'left';
    ctx.fillText('ZEMIN TURLERI', x, y + 12);

    // Tile grid (2 columns)
    const tileSize = 32;
    const gap = 4;
    const cols = 2;

    for (let i = 0; i < TILE_PALETTE.length; i++) {
      const tile = TILE_PALETTE[i];
      const col = i % cols;
      const row = Math.floor(i / cols);

      const tx = x + col * (tileSize + gap + 50);
      const ty = y + 30 + row * (tileSize + gap);

      const isSelected = this.selectedTileType === tile.type;
      const isHovered = this.hoveredTile === tile.type;

      // Tile preview (isometric diamond)
      ctx.fillStyle = tile.color;
      ctx.beginPath();
      ctx.moveTo(tx + tileSize / 2, ty);
      ctx.lineTo(tx + tileSize, ty + tileSize / 2);
      ctx.lineTo(tx + tileSize / 2, ty + tileSize);
      ctx.lineTo(tx, ty + tileSize / 2);
      ctx.closePath();
      ctx.fill();

      // Selection/hover border
      if (isSelected) {
        ctx.strokeStyle = '#d4af37';
        ctx.lineWidth = 2;
      } else if (isHovered) {
        ctx.strokeStyle = '#808080';
        ctx.lineWidth = 1;
      } else {
        ctx.strokeStyle = '#3d3228';
        ctx.lineWidth = 1;
      }
      ctx.stroke();

      // Tile name
      ctx.fillStyle = isSelected ? '#d4af37' : '#808080';
      ctx.font = '10px Arial';
      ctx.textAlign = 'left';
      ctx.fillText(tile.name, tx + tileSize + 5, ty + tileSize / 2 + 3);

      // Walkable indicator
      if (!tile.walkable) {
        ctx.fillStyle = '#ff4444';
        ctx.font = '8px Arial';
        ctx.fillText('X', tx + tileSize / 2 - 3, ty + tileSize / 2 + 3);
      }
    }

    // Brush size slider
    const sliderY = y + 30 + Math.ceil(TILE_PALETTE.length / cols) * (tileSize + gap) + 20;
    ctx.fillStyle = '#808080';
    ctx.font = '11px Arial';
    ctx.textAlign = 'left';
    ctx.fillText(`Firca Boyutu: ${this.brushSize}`, x, sliderY);

    // Slider track
    const sliderX = x;
    const sliderW = width - 20;
    const sliderTrackY = sliderY + 15;

    ctx.fillStyle = '#2a2520';
    this.drawRoundedRect(ctx, sliderX, sliderTrackY, sliderW, 8, 4);
    ctx.fill();

    // Slider fill
    const fillW = ((this.brushSize - 1) / 4) * sliderW;
    ctx.fillStyle = '#d4af37';
    this.drawRoundedRect(ctx, sliderX, sliderTrackY, fillW, 8, 4);
    ctx.fill();

    // Slider handle
    ctx.beginPath();
    ctx.arc(sliderX + fillW, sliderTrackY + 4, 6, 0, Math.PI * 2);
    ctx.fillStyle = '#d4af37';
    ctx.fill();
    ctx.strokeStyle = '#1a1510';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Fill All button
    const fillBtnY = sliderTrackY + 30;
    const fillBtnW = width - 20;
    const fillBtnH = 28;

    ctx.fillStyle = '#2a3a2a';
    this.drawRoundedRect(ctx, x, fillBtnY, fillBtnW, fillBtnH, 4);
    ctx.fill();

    ctx.strokeStyle = '#4a8a4a';
    ctx.lineWidth = 1;
    ctx.stroke();

    ctx.fillStyle = '#6aba6a';
    ctx.font = 'bold 11px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('Tümünü Doldur', x + fillBtnW / 2, fillBtnY + fillBtnH / 2 + 4);
  }

  private renderPropPalette(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, _height: number): void {
    // Category tabs
    const tabHeight = 24;
    const tabWidth = (width / PROP_CATEGORIES.length);

    for (let i = 0; i < PROP_CATEGORIES.length; i++) {
      const cat = PROP_CATEGORIES[i];
      const tabX = x + i * tabWidth;
      const isSelected = this.propCategoryIndex === i;

      ctx.fillStyle = isSelected ? '#2a2520' : '#151210';
      ctx.fillRect(tabX, y, tabWidth - 2, tabHeight);

      ctx.fillStyle = isSelected ? '#d4af37' : '#606060';
      ctx.font = '9px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(cat.name, tabX + tabWidth / 2 - 1, y + tabHeight / 2 + 3);
    }

    // Props in current category
    const category = PROP_CATEGORIES[this.propCategoryIndex];
    const propSize = 40;
    const gap = 8;
    const cols = 3;
    const propsY = y + tabHeight + 15;

    for (let i = 0; i < category.props.length; i++) {
      const propType = category.props[i];
      const col = i % cols;
      const row = Math.floor(i / cols);

      const px = x + col * (propSize + gap);
      const py = propsY + row * (propSize + gap + 15);

      const isSelected = this.selectedPropType === propType;
      const isHovered = this.hoveredProp === propType;

      // Prop background
      ctx.fillStyle = isSelected ? '#3d3228' : (isHovered ? '#2a2520' : '#1a1510');
      this.drawRoundedRect(ctx, px, py, propSize, propSize, 4);
      ctx.fill();

      if (isSelected) {
        ctx.strokeStyle = '#d4af37';
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      // Render prop preview (simplified)
      ctx.save();
      ctx.beginPath();
      ctx.rect(px + 2, py + 2, propSize - 4, propSize - 4);
      ctx.clip();
      PropRegistry.render(ctx, propType, px + propSize / 2, py + propSize - 5, 0.4, 0);
      ctx.restore();

      // Prop name
      ctx.fillStyle = isSelected ? '#d4af37' : '#808080';
      ctx.font = '8px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(propType.replace('_', ' '), px + propSize / 2, py + propSize + 10);
    }

    // Variant selector
    const variantY = propsY + Math.ceil(category.props.length / cols) * (propSize + gap + 15) + 10;
    ctx.fillStyle = '#808080';
    ctx.font = '11px Arial';
    ctx.textAlign = 'left';
    ctx.fillText(`Varyant: ${this.propVariant}`, x, variantY);

    // Variant buttons
    for (let v = 0; v < 3; v++) {
      const vx = x + 70 + v * 25;
      const isVarSelected = this.propVariant === v;

      ctx.fillStyle = isVarSelected ? '#d4af37' : '#2a2520';
      this.drawRoundedRect(ctx, vx, variantY - 10, 20, 16, 3);
      ctx.fill();

      ctx.fillStyle = isVarSelected ? '#1a1510' : '#808080';
      ctx.font = '10px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(`${v}`, vx + 10, variantY);
    }
  }

  private renderSpawnSettings(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, _height: number): void {
    // Title
    ctx.fillStyle = '#d4af37';
    ctx.font = 'bold 12px Arial';
    ctx.textAlign = 'left';
    ctx.fillText('SPAWN AYARLARI', x, y + 12);

    // Spawn type buttons
    const types: { type: SpawnAreaType; name: string; color: string }[] = [
      { type: 'enemy', name: 'Düşman', color: '#ff4444' },
      { type: 'player', name: 'Oyuncu', color: '#44ff44' },
      { type: 'boss', name: 'Boss', color: '#ff44ff' },
    ];

    const btnW = (width - 10) / 3;
    const btnH = 28;
    let panelY = y + 30;

    for (let i = 0; i < types.length; i++) {
      const t = types[i];
      const bx = x + i * (btnW + 5);
      const isSelected = this.selectedSpawnType === t.type;

      ctx.fillStyle = isSelected ? t.color + '40' : '#1a1510';
      this.drawRoundedRect(ctx, bx, panelY, btnW, btnH, 4);
      ctx.fill();

      ctx.strokeStyle = isSelected ? t.color : '#3d3228';
      ctx.lineWidth = isSelected ? 2 : 1;
      ctx.stroke();

      ctx.fillStyle = isSelected ? t.color : '#808080';
      ctx.font = '10px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(t.name, bx + btnW / 2, panelY + btnH / 2 + 3);
    }
    ctx.textAlign = 'left';
    panelY += btnH + 15;

    // Difficulty slider (for enemy spawns)
    if (this.selectedSpawnType === 'enemy') {
      ctx.fillStyle = '#808080';
      ctx.font = '11px Arial';
      ctx.fillText(`Zorluk: ${this.spawnDifficulty}`, x, panelY);

      // Slider
      const sliderW = width - 10;
      const sliderY = panelY + 12;

      ctx.fillStyle = '#2a2520';
      this.drawRoundedRect(ctx, x, sliderY, sliderW, 8, 4);
      ctx.fill();

      const fillW = ((this.spawnDifficulty - 1) / 9) * sliderW;

      // Color gradient based on difficulty
      const gradient = ctx.createLinearGradient(x, sliderY, x + sliderW, sliderY);
      gradient.addColorStop(0, '#44ff44');
      gradient.addColorStop(0.5, '#ffff44');
      gradient.addColorStop(1, '#ff4444');
      ctx.fillStyle = gradient;
      this.drawRoundedRect(ctx, x, sliderY, fillW, 8, 4);
      ctx.fill();
      panelY += 35;
    }

    // Brush size for spawn area
    ctx.fillStyle = '#808080';
    ctx.font = '11px Arial';
    ctx.fillText(`Alan Boyutu: ${this.brushSize}`, x, panelY);
    panelY += 25;

    // Enemy type selection (for enemy/boss spawns)
    if (this.selectedSpawnType === 'enemy' || this.selectedSpawnType === 'boss') {
      ctx.fillStyle = '#d4af37';
      ctx.font = 'bold 11px Arial';
      ctx.fillText('DÜŞMAN TÜRLERİ', x, panelY);
      panelY += 15;

      ctx.fillStyle = '#606060';
      ctx.font = '9px Arial';
      ctx.fillText('Zone ayarlarından belirlenir', x, panelY);
      panelY += 5;

      // Show enemies for current act
      const act = this.mapData.zoneConfig?.act || 1;
      const actEnemies = ENEMY_TYPES.filter(e => e.act === act);

      ctx.fillStyle = '#808080';
      ctx.font = '9px Arial';
      panelY += 15;

      for (const enemy of actEnemies.slice(0, 6)) {
        ctx.fillText(`• ${enemy.name}`, x + 5, panelY);
        panelY += 12;
      }
      if (actEnemies.length > 6) {
        ctx.fillText(`  +${actEnemies.length - 6} daha...`, x + 5, panelY);
      }
    }
  }

  private renderPortalSettings(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, _height: number): void {
    // Title
    ctx.fillStyle = '#d4af37';
    ctx.font = 'bold 12px Arial';
    ctx.textAlign = 'left';
    ctx.fillText('PORTAL AYARLARI', x, y + 12);

    let panelY = y + 30;

    // Portal type selection
    ctx.fillStyle = '#808080';
    ctx.font = '10px Arial';
    ctx.fillText('Portal Tipi:', x, panelY);
    panelY += 8;

    for (const pt of PORTAL_TYPES) {
      const isSelected = this.selectedPortalType === pt.type;
      const btnH = 28;

      ctx.fillStyle = isSelected ? '#3d3228' : '#1a1510';
      this.drawRoundedRect(ctx, x, panelY, width, btnH, 4);
      ctx.fill();

      if (isSelected) {
        ctx.strokeStyle = '#aa66ff';
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      ctx.font = '14px Arial';
      ctx.fillStyle = '#ffffff';
      ctx.fillText(pt.icon, x + 10, panelY + btnH / 2 + 5);

      ctx.fillStyle = isSelected ? '#aa66ff' : '#c0c0c0';
      ctx.font = '11px Arial';
      ctx.fillText(pt.name, x + 35, panelY + btnH / 2 + 4);

      panelY += btnH + 4;
    }

    panelY += 10;

    // Target zone selection
    ctx.fillStyle = '#d4af37';
    ctx.font = 'bold 11px Arial';
    ctx.fillText('HEDEF ZONE', x, panelY);
    panelY += 15;

    // Group by act
    for (let act = 1; act <= 4; act++) {
      const actZones = ZONE_TYPES.filter(z => z.act === act);

      ctx.fillStyle = '#606060';
      ctx.font = 'bold 9px Arial';
      ctx.fillText(`Act ${act}:`, x, panelY);
      panelY += 12;

      for (const zone of actZones) {
        const isSelected = this.selectedTargetZone === zone.zone;

        ctx.fillStyle = isSelected ? '#2a4a6a' : 'transparent';
        ctx.fillRect(x, panelY - 10, width, 14);

        ctx.fillStyle = isSelected ? '#4a9eff' : '#808080';
        ctx.font = '9px Arial';
        ctx.fillText(zone.name, x + 8, panelY);
        panelY += 14;
      }
      panelY += 5;
    }

    // Required level
    panelY += 5;
    ctx.fillStyle = '#808080';
    ctx.font = '10px Arial';
    ctx.fillText(`Gerekli Seviye: ${this.portalRequiredLevel}`, x, panelY);
  }

  private renderMapView(ctx: CanvasRenderingContext2D, layout: ReturnType<typeof this.getLayout>): void {
    const { x, y, width, height } = layout.mapView;

    // Background
    ctx.fillStyle = '#0a0805';
    ctx.fillRect(x, y, width, height);

    // Clip to map view
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, y, width, height);
    ctx.clip();

    // Calculate tile rendering
    const tileW = 32 * this.mapZoom;
    const tileH = 16 * this.mapZoom;

    // Center offset
    const centerX = x + width / 2 + this.mapOffsetX;
    const centerY = y + 50 + this.mapOffsetY;

    // Draw tiles
    for (let ty = 0; ty < this.mapData.height; ty++) {
      for (let tx = 0; tx < this.mapData.width; tx++) {
        const tileType = this.mapData.tiles[ty][tx];
        const tile = TILE_PALETTE.find(t => t.type === tileType);

        // Isometric position
        const isoX = centerX + (tx - ty) * (tileW / 2);
        const isoY = centerY + (tx + ty) * (tileH / 2);

        // Skip if outside view
        if (isoX < x - tileW || isoX > x + width + tileW ||
            isoY < y - tileH || isoY > y + height + tileH) {
          continue;
        }

        // Draw isometric tile
        ctx.fillStyle = tile?.color || '#333';
        ctx.beginPath();
        ctx.moveTo(isoX, isoY - tileH / 2);
        ctx.lineTo(isoX + tileW / 2, isoY);
        ctx.lineTo(isoX, isoY + tileH / 2);
        ctx.lineTo(isoX - tileW / 2, isoY);
        ctx.closePath();
        ctx.fill();

        // Grid lines
        if (this.showGrid) {
          ctx.strokeStyle = '#ffffff10';
          ctx.lineWidth = 1;
          ctx.stroke();
        }
      }
    }

    // Draw spawn areas
    if (this.showSpawnAreas) {
      for (const spawn of this.mapData.spawnAreas) {
        const isoX = centerX + (spawn.x - spawn.y) * (tileW / 2);
        const isoY = centerY + (spawn.x + spawn.y) * (tileH / 2);

        const spawnColor = spawn.type === 'enemy' ? '#ff4444' :
                          spawn.type === 'player' ? '#44ff44' : '#ff44ff';

        ctx.fillStyle = spawnColor + '30';
        ctx.beginPath();
        ctx.ellipse(isoX, isoY, spawn.radius * tileW / 2, spawn.radius * tileH / 2, 0, 0, Math.PI * 2);
        ctx.fill();

        ctx.strokeStyle = spawnColor + '80';
        ctx.lineWidth = 2;
        ctx.setLineDash([4, 4]);
        ctx.stroke();
        ctx.setLineDash([]);
      }
    }

    // Draw props
    for (let i = 0; i < this.mapData.props.length; i++) {
      const prop = this.mapData.props[i];
      const isoX = centerX + (prop.x - prop.y) * (tileW / 2);
      const isoY = centerY + (prop.x + prop.y) * (tileH / 2);

      // Selection highlight
      if (this.selectedElement?.type === 'prop' && this.selectedElement.index === i) {
        ctx.strokeStyle = '#ffff00';
        ctx.lineWidth = 2;
        ctx.strokeRect(isoX - 15, isoY - 30, 30, 35);
      }

      PropRegistry.render(ctx, prop.type, isoX, isoY, this.mapZoom * 0.8, prop.variant);
    }

    // Draw NPCs
    for (let i = 0; i < (this.mapData.npcs || []).length; i++) {
      const npc = this.mapData.npcs[i];
      const isoX = centerX + (npc.x - npc.y) * (tileW / 2);
      const isoY = centerY + (npc.x + npc.y) * (tileH / 2);

      const npcInfo = NPC_TYPES.find(n => n.type === npc.type);
      const isSelected = this.selectedElement?.type === 'npc' && this.selectedElement.index === i;

      // NPC circle background
      ctx.fillStyle = isSelected ? '#ffaa0060' : '#00aa0060';
      ctx.beginPath();
      ctx.arc(isoX, isoY - 10, 14, 0, Math.PI * 2);
      ctx.fill();

      ctx.strokeStyle = isSelected ? '#ffff00' : '#00ff00';
      ctx.lineWidth = 2;
      ctx.stroke();

      // NPC icon
      ctx.font = '14px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#ffffff';
      ctx.fillText(npcInfo?.icon || '👤', isoX, isoY - 10);

      // NPC name
      ctx.fillStyle = '#ffffff';
      ctx.font = '9px Arial';
      ctx.textBaseline = 'alphabetic';
      ctx.fillText(npc.name, isoX, isoY + 10);
    }

    // Draw waypoints
    for (let i = 0; i < this.mapData.waypoints.length; i++) {
      const wp = this.mapData.waypoints[i];
      const isoX = centerX + (wp.x - wp.y) * (tileW / 2);
      const isoY = centerY + (wp.x + wp.y) * (tileH / 2);

      const wpInfo = WAYPOINT_FUNCTIONS.find(w => w.func === wp.function);
      const isSelected = this.selectedElement?.type === 'waypoint' && this.selectedElement.index === i;

      // Waypoint marker
      ctx.fillStyle = isSelected ? '#ffaa00' : '#4488ff';
      ctx.beginPath();
      ctx.moveTo(isoX, isoY - 20);
      ctx.lineTo(isoX - 8, isoY - 8);
      ctx.lineTo(isoX, isoY);
      ctx.lineTo(isoX + 8, isoY - 8);
      ctx.closePath();
      ctx.fill();

      ctx.strokeStyle = isSelected ? '#ffff00' : '#88aaff';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Function icon
      ctx.font = '12px Arial';
      ctx.textAlign = 'center';
      ctx.fillStyle = '#ffffff';
      ctx.fillText(wpInfo?.icon || '📍', isoX, isoY - 12);

      // Name label
      ctx.fillStyle = '#ffffff';
      ctx.font = '9px Arial';
      ctx.fillText(wp.name, isoX, isoY - 26);
    }

    // Draw portals
    for (let i = 0; i < this.mapData.portals.length; i++) {
      const portal = this.mapData.portals[i];
      const isoX = centerX + (portal.x - portal.y) * (tileW / 2);
      const isoY = centerY + (portal.x + portal.y) * (tileH / 2);

      const isSelected = this.selectedElement?.type === 'portal' && this.selectedElement.index === i;

      // Portal type colors
      const portalColors = {
        path: { fill: '#44884430', stroke: '#66aa66' },
        portal: { fill: '#8844ff40', stroke: '#aa66ff' },
        gate: { fill: '#ff884430', stroke: '#ffaa66' },
      };
      const colors = portalColors[portal.portalType] || portalColors.portal;

      // Portal effect
      ctx.fillStyle = isSelected ? '#ffaa0060' : colors.fill;
      ctx.beginPath();
      ctx.ellipse(isoX, isoY - 10, 15, 20, 0, 0, Math.PI * 2);
      ctx.fill();

      ctx.strokeStyle = isSelected ? '#ffff00' : colors.stroke;
      ctx.lineWidth = isSelected ? 3 : 2;
      ctx.stroke();

      // Portal type icon
      const portalIcon = portal.portalType === 'path' ? '🚶' : portal.portalType === 'gate' ? '🚪' : '🌀';
      ctx.font = '12px Arial';
      ctx.textAlign = 'center';
      ctx.fillStyle = '#ffffff';
      ctx.fillText(portalIcon, isoX, isoY - 8);

      // Target zone label
      const zoneInfo = ZONE_TYPES.find(z => z.zone === portal.targetZone);
      ctx.fillStyle = colors.stroke;
      ctx.font = '8px Arial';
      ctx.fillText(`→ ${zoneInfo?.name || portal.targetZone}`, isoX, isoY + 18);

      // Required level
      if (portal.requiresLevel && portal.requiresLevel > 1) {
        ctx.fillStyle = '#ffaa44';
        ctx.font = '7px Arial';
        ctx.fillText(`Lv.${portal.requiresLevel}`, isoX, isoY + 28);
      }
    }

    ctx.restore();

    // Border
    ctx.strokeStyle = '#3d3228';
    ctx.lineWidth = 1;
    ctx.strokeRect(x, y, width, height);

    // Zoom indicator
    ctx.fillStyle = '#808080';
    ctx.font = '10px Arial';
    ctx.textAlign = 'right';
    ctx.fillText(`Zoom: ${Math.round(this.mapZoom * 100)}%`, x + width - 10, y + height - 10);

    // Draw expand buttons on edges
    this.renderExpandButtons(ctx, x, y, width, height);
  }

  private renderExpandButtons(ctx: CanvasRenderingContext2D, viewX: number, viewY: number, viewW: number, viewH: number): void {
    const btnSize = 36;
    const margin = 8;

    const buttons: { dir: 'north' | 'south' | 'east' | 'west'; x: number; y: number; label: string }[] = [
      { dir: 'north', x: viewX + viewW / 2 - btnSize / 2, y: viewY + margin, label: '▲+' },
      { dir: 'south', x: viewX + viewW / 2 - btnSize / 2, y: viewY + viewH - btnSize - margin, label: '▼+' },
      { dir: 'west', x: viewX + margin, y: viewY + viewH / 2 - btnSize / 2, label: '◄+' },
      { dir: 'east', x: viewX + viewW - btnSize - margin, y: viewY + viewH / 2 - btnSize / 2, label: '►+' },
    ];

    for (const btn of buttons) {
      const isHovered = this.hoveredExpandDir === btn.dir;

      // Button background
      ctx.fillStyle = isHovered ? '#4a9eff40' : '#1a1a1a80';
      this.drawRoundedRect(ctx, btn.x, btn.y, btnSize, btnSize, 8);
      ctx.fill();

      // Border
      ctx.strokeStyle = isHovered ? '#4a9eff' : '#4a4a4a';
      ctx.lineWidth = isHovered ? 2 : 1;
      ctx.stroke();

      // Icon
      ctx.fillStyle = isHovered ? '#4a9eff' : '#808080';
      ctx.font = `bold ${isHovered ? 16 : 14}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(btn.label, btn.x + btnSize / 2, btn.y + btnSize / 2);
    }
  }

  // Get expand button at position (returns direction or null)
  private getExpandButtonAt(mx: number, my: number, layout: ReturnType<typeof this.getLayout>): 'north' | 'south' | 'east' | 'west' | null {
    const { x, y, width, height } = layout.mapView;
    const btnSize = 36;
    const margin = 8;

    const buttons: { dir: 'north' | 'south' | 'east' | 'west'; x: number; y: number }[] = [
      { dir: 'north', x: x + width / 2 - btnSize / 2, y: y + margin },
      { dir: 'south', x: x + width / 2 - btnSize / 2, y: y + height - btnSize - margin },
      { dir: 'west', x: x + margin, y: y + height / 2 - btnSize / 2 },
      { dir: 'east', x: x + width - btnSize - margin, y: y + height / 2 - btnSize / 2 },
    ];

    for (const btn of buttons) {
      if (mx >= btn.x && mx <= btn.x + btnSize && my >= btn.y && my <= btn.y + btnSize) {
        return btn.dir;
      }
    }
    return null;
  }

  private renderRightPanel(ctx: CanvasRenderingContext2D, layout: ReturnType<typeof this.getLayout>): void {
    const { x, y, width, height } = layout.rightPanel;

    // Background
    ctx.fillStyle = '#0d0a08';
    ctx.fillRect(x, y, width, height);

    // Border
    ctx.strokeStyle = '#3d3228';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x, y + height);
    ctx.stroke();

    // Zone Config Header
    ctx.fillStyle = '#d4af37';
    ctx.font = 'bold 12px Arial';
    ctx.textAlign = 'left';
    ctx.fillText('ZONE AYARLARI', x + 15, y + 20);

    // Toggle zone config panel
    const toggleBtnX = x + width - 50;
    const toggleBtnY = y + 8;
    ctx.fillStyle = this.showZoneConfig ? '#2a6a2a' : '#2a2520';
    this.drawRoundedRect(ctx, toggleBtnX, toggleBtnY, 40, 18, 4);
    ctx.fill();
    ctx.fillStyle = '#c0c0c0';
    ctx.font = '10px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(this.showZoneConfig ? 'Gizle' : 'Göster', toggleBtnX + 20, toggleBtnY + 13);

    let panelY = y + 35;

    if (this.showZoneConfig && this.mapData.zoneConfig) {
      const cfg = this.mapData.zoneConfig;

      // Zone Type
      ctx.fillStyle = '#808080';
      ctx.font = '10px Arial';
      ctx.textAlign = 'left';
      ctx.fillText('Zone Tipi:', x + 15, panelY);
      panelY += 5;

      const zoneInfo = ZONE_TYPES.find(z => z.zone === cfg.zoneType);
      ctx.fillStyle = '#1a1510';
      this.drawRoundedRect(ctx, x + 15, panelY, width - 30, 22, 3);
      ctx.fill();
      ctx.fillStyle = '#c0c0c0';
      ctx.font = '11px Arial';
      ctx.fillText(zoneInfo?.name || cfg.zoneType, x + 20, panelY + 15);
      panelY += 30;

      // Act
      ctx.fillStyle = '#808080';
      ctx.font = '10px Arial';
      ctx.fillText('Act:', x + 15, panelY);
      const actBtnW = 35;
      for (let act = 1; act <= 4; act++) {
        const actX = x + 50 + (act - 1) * (actBtnW + 5);
        ctx.fillStyle = cfg.act === act ? '#3d6a3d' : '#1a1510';
        this.drawRoundedRect(ctx, actX, panelY - 12, actBtnW, 18, 3);
        ctx.fill();
        ctx.fillStyle = cfg.act === act ? '#44ff44' : '#808080';
        ctx.font = '11px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(`${act}`, actX + actBtnW / 2, panelY + 2);
      }
      ctx.textAlign = 'left';
      panelY += 22;

      // Level Range
      ctx.fillStyle = '#808080';
      ctx.font = '10px Arial';
      ctx.fillText(`Seviye: ${cfg.levelRange.min} - ${cfg.levelRange.max}`, x + 15, panelY);
      panelY += 18;

      // Level slider
      const sliderW = width - 50;
      ctx.fillStyle = '#1a1510';
      this.drawRoundedRect(ctx, x + 15, panelY - 5, sliderW, 8, 4);
      ctx.fill();
      const minPct = (cfg.levelRange.min - 1) / 49;
      const maxPct = (cfg.levelRange.max - 1) / 49;
      ctx.fillStyle = '#3d6a3d';
      ctx.fillRect(x + 15 + minPct * sliderW, panelY - 5, (maxPct - minPct) * sliderW, 8);
      panelY += 18;

      // Safe Zone Toggle
      this.renderToggle(ctx, x + 15, panelY, 'Güvenli Bölge', cfg.isSafeZone);
      panelY += 30;

      // Max Enemies & Spawn Interval
      ctx.fillStyle = '#808080';
      ctx.font = '10px Arial';
      ctx.fillText(`Max Düşman: ${cfg.maxEnemies}`, x + 15, panelY);
      panelY += 15;
      ctx.fillText(`Spawn Aralığı: ${cfg.spawnInterval}sn`, x + 15, panelY);
      panelY += 20;

      // Boss Type
      if (cfg.bossType) {
        const bossInfo = ENEMY_TYPES.find(e => e.type === cfg.bossType);
        ctx.fillStyle = '#ff6644';
        ctx.font = '10px Arial';
        ctx.fillText(`Boss: ${bossInfo?.name || cfg.bossType}`, x + 15, panelY);
        panelY += 15;
      }

      // Ambient Color Preview
      ctx.fillStyle = '#808080';
      ctx.font = '10px Arial';
      ctx.fillText('Ambient:', x + 15, panelY);
      ctx.fillStyle = cfg.ambientColor;
      ctx.fillRect(x + 65, panelY - 10, 20, 14);
      ctx.strokeStyle = '#606060';
      ctx.strokeRect(x + 65, panelY - 10, 20, 14);
      panelY += 20;

      // Separator
      ctx.strokeStyle = '#3d3228';
      ctx.beginPath();
      ctx.moveTo(x + 15, panelY);
      ctx.lineTo(x + width - 15, panelY);
      ctx.stroke();
      panelY += 10;
    }

    // Statistics Header
    ctx.fillStyle = '#d4af37';
    ctx.font = 'bold 11px Arial';
    ctx.textAlign = 'left';
    ctx.fillText('İSTATİSTİKLER', x + 15, panelY + 5);
    panelY += 20;

    ctx.fillStyle = '#808080';
    ctx.font = '10px Arial';
    const stats = [
      `Harita: ${this.mapData.width}x${this.mapData.height}`,
      `Nesneler: ${this.mapData.props.length}`,
      `NPC: ${this.mapData.npcs.length}`,
      `Spawn: ${this.mapData.spawnAreas.length}`,
      `Waypoint: ${this.mapData.waypoints.length}`,
      `Portal: ${this.mapData.portals.length}`,
    ];

    for (const stat of stats) {
      ctx.fillText(stat, x + 15, panelY);
      panelY += 14;
    }
    panelY += 10;

    // Display Options
    ctx.fillStyle = '#d4af37';
    ctx.font = 'bold 11px Arial';
    ctx.fillText('GÖRÜNÜM', x + 15, panelY);
    panelY += 15;

    this.renderToggle(ctx, x + 15, panelY, 'Izgara', this.showGrid);
    panelY += 25;
    this.renderToggle(ctx, x + 15, panelY, 'Spawn Alanları', this.showSpawnAreas);
    panelY += 35;

    // Export Button
    ctx.fillStyle = '#1a4a6a';
    this.drawRoundedRect(ctx, x + 15, panelY, width - 30, 28, 4);
    ctx.fill();
    ctx.strokeStyle = '#4a9eff';
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.fillStyle = '#4a9eff';
    ctx.font = 'bold 11px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('Zone Olarak Dışa Aktar', x + width / 2, panelY + 18);
    ctx.textAlign = 'left';
  }

  private renderToggle(ctx: CanvasRenderingContext2D, x: number, y: number, label: string, value: boolean): void {
    // Toggle track
    const trackW = 36;
    const trackH = 18;

    ctx.fillStyle = value ? '#2a6a2a' : '#2a2520';
    this.drawRoundedRect(ctx, x, y, trackW, trackH, trackH / 2);
    ctx.fill();

    // Toggle knob
    const knobX = value ? x + trackW - trackH / 2 - 2 : x + trackH / 2 + 2;
    ctx.fillStyle = value ? '#44ff44' : '#606060';
    ctx.beginPath();
    ctx.arc(knobX, y + trackH / 2, trackH / 2 - 3, 0, Math.PI * 2);
    ctx.fill();

    // Label
    ctx.fillStyle = '#808080';
    ctx.font = '11px Arial';
    ctx.textAlign = 'left';
    ctx.fillText(label, x + trackW + 10, y + trackH / 2 + 4);
  }

  private renderBottomBar(ctx: CanvasRenderingContext2D, layout: ReturnType<typeof this.getLayout>): void {
    const { x, y, width, height } = layout.bottomBar;

    // Background
    ctx.fillStyle = '#151210';
    ctx.fillRect(x, y, width, height);

    // Instructions
    ctx.fillStyle = '#606060';
    ctx.font = '11px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(
      'Sol Tik: Yerle\u015Ftir/Boya | Sa\u011F Tik: Sil | Orta Tik + Surukle: Kayd\u0131r | Scroll: Zoom',
      x + width / 2, y + height / 2 + 3
    );
  }

  private drawButton(
    ctx: CanvasRenderingContext2D,
    x: number, y: number, w: number, h: number,
    text: string, bgColor: string, textColor: string
  ): void {
    ctx.fillStyle = bgColor;
    this.drawRoundedRect(ctx, x, y, w, h, 4);
    ctx.fill();

    ctx.strokeStyle = textColor + '80';
    ctx.lineWidth = 1;
    ctx.stroke();

    ctx.fillStyle = textColor;
    ctx.font = 'bold 11px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, x + w / 2, y + h / 2);
  }

  // Mouse handling
  handleMouseEvent(e: import('../components/UIComponent').MouseEvent): boolean {
    if (!this.visible) return false;

    // Handle load dialog first if open
    if (this.showLoadDialog) {
      return this.handleLoadDialogMouse(e);
    }

    const layout = this.getLayout();

    // Handle different areas
    if (this.isInRect(e.x, e.y, layout.toolbar)) {
      return this.handleToolbarMouse(e, layout);
    }

    if (this.isInRect(e.x, e.y, layout.leftPanel)) {
      return this.handleLeftPanelMouse(e, layout);
    }

    if (this.isInRect(e.x, e.y, layout.mapView)) {
      return this.handleMapViewMouse(e, layout);
    }

    if (this.isInRect(e.x, e.y, layout.rightPanel)) {
      return this.handleRightPanelMouse(e, layout);
    }

    if (this.isInRect(e.x, e.y, layout.topBar)) {
      return this.handleTopBarMouse(e, layout);
    }

    return false;
  }

  private handleLoadDialogMouse(e: import('../components/UIComponent').MouseEvent): boolean {
    const { x, y, width, height } = this.bounds;

    const dialogW = 400;
    const dialogH = 350;
    const dialogX = x + (width - dialogW) / 2;
    const dialogY = y + (height - dialogH) / 2;

    const listX = dialogX + 20;
    const listY = dialogY + 50;
    const listW = dialogW - 40;
    const listH = dialogH - 100;
    const itemH = 50;

    // Handle scroll in list
    if (e.type === 'wheel' && e.deltaY !== undefined) {
      if (e.x >= listX && e.x <= listX + listW && e.y >= listY && e.y <= listY + listH) {
        const totalHeight = this.savedMapsList.length * itemH;
        const maxScroll = Math.max(0, totalHeight - listH);
        this.loadDialogScrollY = Math.max(0, Math.min(maxScroll, this.loadDialogScrollY + e.deltaY * 0.5));
        return true;
      }
    }

    // Handle hover in list
    if (e.type === 'move') {
      this.hoveredMapIndex = -1;
      if (e.x >= listX && e.x <= listX + listW && e.y >= listY && e.y <= listY + listH) {
        for (let i = 0; i < this.savedMapsList.length; i++) {
          const itemY = listY + 5 + i * itemH - this.loadDialogScrollY;
          if (e.y >= itemY && e.y <= itemY + itemH - 5) {
            this.hoveredMapIndex = i;
            break;
          }
        }
      }
    }

    if (e.type === 'down' && e.button === 'left') {
      // Close button
      if (e.x >= dialogX + dialogW - 30 && e.x <= dialogX + dialogW - 10 &&
          e.y >= dialogY + 10 && e.y <= dialogY + 35) {
        this.closeLoadDialog();
        return true;
      }

      // Cancel button
      const btnW = 100;
      const btnH = 30;
      const cancelX = dialogX + dialogW / 2 - btnW / 2;
      const cancelY = dialogY + dialogH - 45;

      if (e.x >= cancelX && e.x <= cancelX + btnW && e.y >= cancelY && e.y <= cancelY + btnH) {
        this.closeLoadDialog();
        return true;
      }

      // Click on map item in list
      if (e.x >= listX && e.x <= listX + listW && e.y >= listY && e.y <= listY + listH) {
        for (let i = 0; i < this.savedMapsList.length; i++) {
          const itemY = listY + 5 + i * itemH - this.loadDialogScrollY;
          if (e.y >= itemY && e.y <= itemY + itemH - 5) {
            const map = this.savedMapsList[i];
            this.loadMapById(map.id);
            this.closeLoadDialog();
            return true;
          }
        }
      }

      // Click outside dialog to close
      if (e.x < dialogX || e.x > dialogX + dialogW || e.y < dialogY || e.y > dialogY + dialogH) {
        this.closeLoadDialog();
        return true;
      }
    }

    return true; // Consume all events when dialog is open
  }

  private isInRect(mx: number, my: number, rect: { x: number; y: number; width: number; height: number }): boolean {
    return mx >= rect.x && mx <= rect.x + rect.width && my >= rect.y && my <= rect.y + rect.height;
  }

  private handleToolbarMouse(e: import('../components/UIComponent').MouseEvent, layout: ReturnType<typeof this.getLayout>): boolean {
    const { x, y } = layout.toolbar;
    const toolSize = 44;
    const padding = 8;

    // Reset hover
    if (e.type === 'move') {
      this.hoveredTool = null;
    }

    let toolY = y + padding;
    for (const tool of TOOLS) {
      if (e.x >= x + padding && e.x <= x + padding + toolSize &&
          e.y >= toolY && e.y <= toolY + toolSize) {

        if (e.type === 'move') {
          this.hoveredTool = tool.id;
        } else if (e.type === 'down' && e.button === 'left') {
          this.currentTool = tool.id;
          return true;
        }
      }
      toolY += toolSize + 4;
    }

    return false;
  }

  private handleLeftPanelMouse(e: import('../components/UIComponent').MouseEvent, layout: ReturnType<typeof this.getLayout>): boolean {
    const { x, y, width } = layout.leftPanel;
    const panelX = x + 10;
    const panelY = y + 10;

    // Tile palette interaction
    if (this.currentTool === 'tile_brush' || this.currentTool === 'walkable_area') {
      const tileSize = 32;
      const gap = 4;
      const cols = 2;

      if (e.type === 'move') {
        this.hoveredTile = null;
      }

      for (let i = 0; i < TILE_PALETTE.length; i++) {
        const tile = TILE_PALETTE[i];
        const col = i % cols;
        const row = Math.floor(i / cols);

        const tx = panelX + col * (tileSize + gap + 50);
        const ty = panelY + 30 + row * (tileSize + gap);

        if (e.x >= tx && e.x <= tx + tileSize && e.y >= ty && e.y <= ty + tileSize) {
          if (e.type === 'move') {
            this.hoveredTile = tile.type;
          } else if (e.type === 'down' && e.button === 'left') {
            this.selectedTileType = tile.type;
            return true;
          }
        }
      }

      // Brush size slider
      const sliderY = panelY + 30 + Math.ceil(TILE_PALETTE.length / cols) * (tileSize + gap) + 35;
      const sliderW = width - 40;

      if (e.y >= sliderY - 5 && e.y <= sliderY + 15 && e.x >= panelX && e.x <= panelX + sliderW) {
        if (e.type === 'down' || (e.type === 'move' && e.button === 'left')) {
          const pct = Math.max(0, Math.min(1, (e.x - panelX) / sliderW));
          this.brushSize = Math.round(pct * 4) + 1;
          return true;
        }
      }

      // Fill All button
      const fillBtnY = sliderY + 30;
      const fillBtnW = width - 40;
      const fillBtnH = 28;

      if (e.type === 'down' && e.button === 'left' &&
          e.x >= panelX && e.x <= panelX + fillBtnW &&
          e.y >= fillBtnY && e.y <= fillBtnY + fillBtnH) {
        this.fillAllTiles(this.selectedTileType);
        return true;
      }
    }

    // Prop palette interaction
    if (this.currentTool === 'prop') {
      const tabHeight = 24;
      const tabWidth = (width - 20) / PROP_CATEGORIES.length;

      // Category tabs
      for (let i = 0; i < PROP_CATEGORIES.length; i++) {
        const tabX = panelX + i * tabWidth;

        if (e.x >= tabX && e.x <= tabX + tabWidth && e.y >= panelY && e.y <= panelY + tabHeight) {
          if (e.type === 'down' && e.button === 'left') {
            this.propCategoryIndex = i;
            return true;
          }
        }
      }

      // Props
      const propSize = 40;
      const gap = 8;
      const cols = 3;
      const propsY = panelY + tabHeight + 15;
      const category = PROP_CATEGORIES[this.propCategoryIndex];

      if (e.type === 'move') {
        this.hoveredProp = null;
      }

      for (let i = 0; i < category.props.length; i++) {
        const propType = category.props[i];
        const col = i % cols;
        const row = Math.floor(i / cols);

        const px = panelX + col * (propSize + gap);
        const py = propsY + row * (propSize + gap + 15);

        if (e.x >= px && e.x <= px + propSize && e.y >= py && e.y <= py + propSize) {
          if (e.type === 'move') {
            this.hoveredProp = propType;
          } else if (e.type === 'down' && e.button === 'left') {
            this.selectedPropType = propType;
            return true;
          }
        }
      }

      // Variant buttons
      const variantY = propsY + Math.ceil(category.props.length / cols) * (propSize + gap + 15);
      for (let v = 0; v < 3; v++) {
        const vx = panelX + 70 + v * 25;
        if (e.x >= vx && e.x <= vx + 20 && e.y >= variantY - 10 && e.y <= variantY + 6) {
          if (e.type === 'down' && e.button === 'left') {
            this.propVariant = v;
            return true;
          }
        }
      }
    }

    // Spawn settings interaction
    if (this.currentTool === 'spawn_area') {
      const btnW = (width - 30) / 3;
      const btnH = 28;
      const btnY = panelY + 30;

      const types: SpawnAreaType[] = ['enemy', 'player', 'boss'];
      for (let i = 0; i < types.length; i++) {
        const bx = panelX + i * (btnW + 5);
        if (e.x >= bx && e.x <= bx + btnW && e.y >= btnY && e.y <= btnY + btnH) {
          if (e.type === 'down' && e.button === 'left') {
            this.selectedSpawnType = types[i];
            return true;
          }
        }
      }

      // Difficulty slider
      if (this.selectedSpawnType === 'enemy') {
        const diffY = btnY + btnH + 45;
        const sliderW = width - 30;

        if (e.y >= diffY - 5 && e.y <= diffY + 10 && e.x >= panelX && e.x <= panelX + sliderW) {
          if (e.type === 'down' || (e.type === 'move' && e.button === 'left')) {
            const pct = Math.max(0, Math.min(1, (e.x - panelX) / sliderW));
            this.spawnDifficulty = Math.round(pct * 9) + 1;
            return true;
          }
        }
      }
    }

    // NPC type selection
    if (this.currentTool === 'npc') {
      const btnH = 36;
      const gap = 6;
      let btnY = panelY + 30;
      const btnW = width - 20;

      for (const npc of NPC_TYPES) {
        if (e.x >= panelX && e.x <= panelX + btnW && e.y >= btnY && e.y <= btnY + btnH) {
          if (e.type === 'down' && e.button === 'left') {
            this.selectedNPCType = npc.type;
            return true;
          }
        }
        btnY += btnH + gap;
      }
    }

    // Waypoint function selection
    if (this.currentTool === 'waypoint') {
      const btnH = 36;
      const gap = 6;
      let btnY = panelY + 30;
      const btnW = width - 20;

      for (const wp of WAYPOINT_FUNCTIONS) {
        if (e.x >= panelX && e.x <= panelX + btnW && e.y >= btnY && e.y <= btnY + btnH) {
          if (e.type === 'down' && e.button === 'left') {
            this.selectedWaypointFunc = wp.func;
            return true;
          }
        }
        btnY += btnH + gap;
      }
    }

    // Select tool - delete button
    if (this.currentTool === 'select' && this.selectedElement) {
      const btnY = panelY + 35 + 54 + 30; // Approximate position based on renderSelectionInfo
      const btnW = width - 20;
      const btnH = 28;

      if (e.type === 'down' && e.button === 'left' &&
          e.x >= panelX && e.x <= panelX + btnW &&
          e.y >= btnY && e.y <= btnY + btnH) {
        this.deleteSelectedElement();
        return true;
      }
    }

    // Portal settings
    if (this.currentTool === 'portal') {
      let clickY = panelY + 38;
      const btnW = width - 20;

      // Portal type buttons
      for (const pt of PORTAL_TYPES) {
        const btnH = 28;
        if (e.type === 'down' && e.button === 'left' &&
            e.x >= panelX && e.x <= panelX + btnW &&
            e.y >= clickY && e.y <= clickY + btnH) {
          this.selectedPortalType = pt.type;
          return true;
        }
        clickY += btnH + 4;
      }

      clickY += 25; // Skip header

      // Target zone selection
      for (let act = 1; act <= 4; act++) {
        const actZones = ZONE_TYPES.filter(z => z.act === act);
        clickY += 12; // Act header

        for (const zone of actZones) {
          if (e.type === 'down' && e.button === 'left' &&
              e.x >= panelX && e.x <= panelX + btnW &&
              e.y >= clickY - 10 && e.y <= clickY + 4) {
            this.selectedTargetZone = zone.zone;
            return true;
          }
          clickY += 14;
        }
        clickY += 5;
      }
    }

    return false;
  }

  private handleMapViewMouse(e: import('../components/UIComponent').MouseEvent, layout: ReturnType<typeof this.getLayout>): boolean {
    // Check expand buttons first
    const expandBtn = this.getExpandButtonAt(e.x, e.y, layout);

    if (e.type === 'move') {
      this.hoveredExpandDir = expandBtn;
    }

    if (e.type === 'down' && e.button === 'left' && expandBtn) {
      this.expandMap(expandBtn, 10); // Expand 10 tiles in that direction
      return true;
    }

    // Zoom with scroll
    if (e.type === 'wheel' && e.deltaY !== undefined) {
      const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;
      this.mapZoom = Math.max(0.2, Math.min(2, this.mapZoom * zoomFactor));
      return true;
    }

    // Pan with middle mouse or right mouse
    if (e.type === 'down' && (e.button === 'middle' || e.button === 'right')) {
      this.isDraggingMap = true;
      this.lastMouseX = e.x;
      this.lastMouseY = e.y;
      return true;
    }

    if (e.type === 'up') {
      this.isDraggingMap = false;
    }

    if (e.type === 'move' && this.isDraggingMap) {
      this.mapOffsetX += e.x - this.lastMouseX;
      this.mapOffsetY += e.y - this.lastMouseY;
      this.lastMouseX = e.x;
      this.lastMouseY = e.y;
      return true;
    }

    // Convert screen position to tile position
    const tilePos = this.screenToTile(e.x, e.y, layout);

    if (!tilePos || tilePos.x < 0 || tilePos.x >= this.mapData.width ||
        tilePos.y < 0 || tilePos.y >= this.mapData.height) {
      return false;
    }

    // Handle painting/placing with left click only (no drag painting)
    if (e.type === 'down' && e.button === 'left') {
      return this.handleMapAction(tilePos.x, tilePos.y, 'place');
    }

    return false;
  }

  private screenToTile(screenX: number, screenY: number, layout: ReturnType<typeof this.getLayout>): { x: number; y: number } | null {
    const { x, y, width } = layout.mapView;

    const tileW = 32 * this.mapZoom;
    const tileH = 16 * this.mapZoom;

    const centerX = x + width / 2 + this.mapOffsetX;
    const centerY = y + 50 + this.mapOffsetY;

    // Reverse isometric transformation
    const relX = screenX - centerX;
    const relY = screenY - centerY;

    const tileX = Math.floor((relX / (tileW / 2) + relY / (tileH / 2)) / 2);
    const tileY = Math.floor((relY / (tileH / 2) - relX / (tileW / 2)) / 2);

    return { x: tileX, y: tileY };
  }

  private handleMapAction(tileX: number, tileY: number, action: 'place' | 'remove'): boolean {
    switch (this.currentTool) {
      case 'tile_brush':
        return this.paintTiles(tileX, tileY);

      case 'walkable_area':
        return this.paintWalkableArea(tileX, tileY);

      case 'spawn_area':
        if (action === 'place') {
          return this.addSpawnArea(tileX, tileY);
        }
        break;

      case 'prop':
        if (action === 'place') {
          return this.addProp(tileX, tileY);
        }
        break;

      case 'waypoint':
        if (action === 'place') {
          return this.addWaypoint(tileX, tileY);
        }
        break;

      case 'portal':
        if (action === 'place') {
          return this.addPortal(tileX, tileY);
        }
        break;

      case 'npc':
        if (action === 'place') {
          return this.addNPC(tileX, tileY);
        }
        break;

      case 'select':
        return this.selectElementAt(tileX, tileY);

      case 'eraser':
        return this.eraseAt(tileX, tileY);
    }

    return false;
  }

  private paintTiles(centerX: number, centerY: number): boolean {
    const radius = this.brushSize - 1;

    for (let dy = -radius; dy <= radius; dy++) {
      for (let dx = -radius; dx <= radius; dx++) {
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist <= radius + 0.5) {
          const tx = centerX + dx;
          const ty = centerY + dy;

          if (tx >= 0 && tx < this.mapData.width && ty >= 0 && ty < this.mapData.height) {
            this.mapData.tiles[ty][tx] = this.selectedTileType;
          }
        }
      }
    }

    this.notifyMapChange();
    return true;
  }

  private paintWalkableArea(centerX: number, centerY: number): boolean {
    // For walkable area, we paint with the selected tile type
    // Non-walkable tiles (water, lava, void) are marked as non-walkable
    return this.paintTiles(centerX, centerY);
  }

  private addSpawnArea(x: number, y: number): boolean {
    // Check if spawn area already exists at this position
    const existing = this.mapData.spawnAreas.find(s =>
      Math.abs(s.x - x) < 2 && Math.abs(s.y - y) < 2
    );

    if (existing) {
      // Update existing
      existing.type = this.selectedSpawnType;
      existing.radius = this.brushSize;
      existing.difficulty = this.spawnDifficulty;
    } else {
      // Add new
      this.mapData.spawnAreas.push({
        type: this.selectedSpawnType,
        x,
        y,
        radius: this.brushSize,
        difficulty: this.spawnDifficulty,
      });
    }

    this.notifyMapChange();
    return true;
  }

  private addProp(x: number, y: number): boolean {
    // Check if prop already exists at this exact position
    const existing = this.mapData.props.find(p => p.x === x && p.y === y);

    if (existing) {
      // Replace existing
      existing.type = this.selectedPropType;
      existing.variant = this.propVariant;
    } else {
      // Add new
      this.mapData.props.push({
        type: this.selectedPropType,
        x,
        y,
        variant: this.propVariant,
      });
    }

    this.notifyMapChange();
    return true;
  }

  private addWaypoint(x: number, y: number): boolean {
    const id = `wp_${Date.now()}`;
    const funcInfo = WAYPOINT_FUNCTIONS.find(w => w.func === this.selectedWaypointFunc);
    const name = `${funcInfo?.name || 'Waypoint'} ${this.mapData.waypoints.length + 1}`;

    this.mapData.waypoints.push({
      id,
      x,
      y,
      name,
      function: this.selectedWaypointFunc,
    });
    this.notifyMapChange();
    return true;
  }

  private addNPC(x: number, y: number): boolean {
    const id = `npc_${Date.now()}`;
    const npcInfo = NPC_TYPES.find(n => n.type === this.selectedNPCType);
    const name = `${npcInfo?.name || 'NPC'} ${this.mapData.npcs.length + 1}`;

    this.mapData.npcs.push({
      id,
      x,
      y,
      type: this.selectedNPCType,
      name,
    });
    this.notifyMapChange();
    return true;
  }

  private selectElementAt(x: number, y: number): boolean {
    // Check NPCs
    const npcIdx = this.mapData.npcs.findIndex(n =>
      Math.abs(n.x - x) < 2 && Math.abs(n.y - y) < 2
    );
    if (npcIdx >= 0) {
      this.selectedElement = { type: 'npc', index: npcIdx };
      return true;
    }

    // Check waypoints
    const wpIdx = this.mapData.waypoints.findIndex(w =>
      Math.abs(w.x - x) < 2 && Math.abs(w.y - y) < 2
    );
    if (wpIdx >= 0) {
      this.selectedElement = { type: 'waypoint', index: wpIdx };
      return true;
    }

    // Check props
    const propIdx = this.mapData.props.findIndex(p =>
      Math.abs(p.x - x) < 1 && Math.abs(p.y - y) < 1
    );
    if (propIdx >= 0) {
      this.selectedElement = { type: 'prop', index: propIdx };
      return true;
    }

    // Check spawn areas
    const spawnIdx = this.mapData.spawnAreas.findIndex(s =>
      Math.abs(s.x - x) < s.radius && Math.abs(s.y - y) < s.radius
    );
    if (spawnIdx >= 0) {
      this.selectedElement = { type: 'spawn', index: spawnIdx };
      return true;
    }

    // Check portals
    const portalIdx = this.mapData.portals.findIndex(p =>
      Math.abs(p.x - x) < 2 && Math.abs(p.y - y) < 2
    );
    if (portalIdx >= 0) {
      this.selectedElement = { type: 'portal', index: portalIdx };
      return true;
    }

    // Nothing found, clear selection
    this.selectedElement = null;
    return false;
  }

  private deleteSelectedElement(): void {
    if (!this.selectedElement) return;

    const { type, index } = this.selectedElement;

    switch (type) {
      case 'npc':
        if (this.mapData.npcs[index]) {
          this.mapData.npcs.splice(index, 1);
        }
        break;
      case 'waypoint':
        if (this.mapData.waypoints[index]) {
          this.mapData.waypoints.splice(index, 1);
        }
        break;
      case 'prop':
        if (this.mapData.props[index]) {
          this.mapData.props.splice(index, 1);
        }
        break;
      case 'spawn':
        if (this.mapData.spawnAreas[index]) {
          this.mapData.spawnAreas.splice(index, 1);
        }
        break;
      case 'portal':
        if (this.mapData.portals[index]) {
          this.mapData.portals.splice(index, 1);
        }
        break;
    }

    this.selectedElement = null;
    this.notifyMapChange();
  }

  private addPortal(x: number, y: number): boolean {
    this.mapData.portals.push({
      x,
      y,
      targetZone: this.selectedTargetZone,
      portalType: this.selectedPortalType,
      requiresLevel: this.portalRequiredLevel > 1 ? this.portalRequiredLevel : undefined,
    });
    this.notifyMapChange();
    return true;
  }

  private eraseAt(x: number, y: number): boolean {
    let erased = false;

    // Remove props at position
    const propIdx = this.mapData.props.findIndex(p =>
      Math.abs(p.x - x) < 1 && Math.abs(p.y - y) < 1
    );
    if (propIdx >= 0) {
      this.mapData.props.splice(propIdx, 1);
      erased = true;
    }

    // Remove NPCs at position
    const npcIdx = this.mapData.npcs.findIndex(n =>
      Math.abs(n.x - x) < 2 && Math.abs(n.y - y) < 2
    );
    if (npcIdx >= 0) {
      this.mapData.npcs.splice(npcIdx, 1);
      erased = true;
    }

    // Remove spawn areas at position
    const spawnIdx = this.mapData.spawnAreas.findIndex(s =>
      Math.abs(s.x - x) < s.radius && Math.abs(s.y - y) < s.radius
    );
    if (spawnIdx >= 0) {
      this.mapData.spawnAreas.splice(spawnIdx, 1);
      erased = true;
    }

    // Remove waypoints at position
    const wpIdx = this.mapData.waypoints.findIndex(w =>
      Math.abs(w.x - x) < 2 && Math.abs(w.y - y) < 2
    );
    if (wpIdx >= 0) {
      this.mapData.waypoints.splice(wpIdx, 1);
      erased = true;
    }

    // Remove portals at position
    const portalIdx = this.mapData.portals.findIndex(p =>
      Math.abs(p.x - x) < 2 && Math.abs(p.y - y) < 2
    );
    if (portalIdx >= 0) {
      this.mapData.portals.splice(portalIdx, 1);
      erased = true;
    }

    if (erased) {
      this.notifyMapChange();
    }

    return erased;
  }

  private handleRightPanelMouse(e: import('../components/UIComponent').MouseEvent, layout: ReturnType<typeof this.getLayout>): boolean {
    const { x, y, width } = layout.rightPanel;

    if (e.type !== 'down' || e.button !== 'left') return false;

    // Toggle zone config button
    const toggleBtnX = x + width - 50;
    const toggleBtnY = y + 8;
    if (e.x >= toggleBtnX && e.x <= toggleBtnX + 40 && e.y >= toggleBtnY && e.y <= toggleBtnY + 18) {
      this.showZoneConfig = !this.showZoneConfig;
      return true;
    }

    let panelY = y + 35;

    if (this.showZoneConfig && this.mapData.zoneConfig) {
      // Zone type click (opens zone selection later)
      panelY += 27; // Skip zone type display

      // Act selection buttons
      const actBtnW = 35;
      for (let act = 1; act <= 4; act++) {
        const actX = x + 50 + (act - 1) * (actBtnW + 5);
        if (e.x >= actX && e.x <= actX + actBtnW && e.y >= panelY - 12 && e.y <= panelY + 6) {
          this.mapData.zoneConfig.act = act as 1 | 2 | 3 | 4;
          this.notifyMapChange();
          return true;
        }
      }
      panelY += 22;

      // Skip level range display
      panelY += 18;
      panelY += 18;

      // Safe Zone Toggle
      if (e.x >= x + 15 && e.x <= x + 51 && e.y >= panelY && e.y <= panelY + 18) {
        this.mapData.zoneConfig.isSafeZone = !this.mapData.zoneConfig.isSafeZone;
        this.notifyMapChange();
        return true;
      }
      panelY += 90; // Skip to separator
    }

    // Skip to display options
    panelY += 20 + (6 * 14) + 10; // Stats
    panelY += 15; // Display options header

    // Grid toggle
    if (e.x >= x + 15 && e.x <= x + 51 && e.y >= panelY && e.y <= panelY + 18) {
      this.showGrid = !this.showGrid;
      return true;
    }
    panelY += 25;

    // Spawn areas toggle
    if (e.x >= x + 15 && e.x <= x + 51 && e.y >= panelY && e.y <= panelY + 18) {
      this.showSpawnAreas = !this.showSpawnAreas;
      return true;
    }
    panelY += 35;

    // Export button
    if (e.x >= x + 15 && e.x <= x + width - 15 && e.y >= panelY && e.y <= panelY + 28) {
      this.exportAsZoneConfig();
      return true;
    }

    return false;
  }

  private exportAsZoneConfig(): void {
    if (!this.mapData.zoneConfig) return;

    const cfg = this.mapData.zoneConfig;

    // Build enemy spawns from zone's act
    const actEnemies = ENEMY_TYPES.filter(e => e.act === cfg.act && !e.name.includes('Boss'));
    const enemySpawns: EnemySpawnConfig[] = actEnemies.map((e, i) => ({
      type: e.type,
      weight: Math.max(10, 40 - i * 5),
    }));

    // Build exits from portals
    const exits = this.mapData.portals.map(p => ({
      targetZone: p.targetZone,
      position: { x: p.x * 32, y: p.y * 32 }, // Convert tile to world position
      portalType: p.portalType,
      requiresLevel: p.requiresLevel,
    }));

    // Create zone config export
    const zoneExport = {
      id: cfg.zoneType,
      name: cfg.zoneName,
      description: cfg.description,
      tilesetId: cfg.tilesetId,
      ambientColor: cfg.ambientColor,
      fogColor: cfg.fogColor,
      fogDensity: cfg.fogDensity,
      levelRange: cfg.levelRange,
      isSafeZone: cfg.isSafeZone,
      enemySpawns,
      maxEnemies: cfg.maxEnemies,
      spawnInterval: cfg.spawnInterval,
      bossType: cfg.bossType,
      musicTheme: cfg.musicTheme,
      ambientSounds: cfg.ambientSounds,
      exits,
      // Map data
      mapWidth: this.mapData.width,
      mapHeight: this.mapData.height,
      tiles: this.mapData.tiles,
      props: this.mapData.props,
      npcs: this.mapData.npcs,
      waypoints: this.mapData.waypoints,
      spawnAreas: this.mapData.spawnAreas,
    };

    // Export to console (can be copied)
    console.log('=== Zone Export ===');
    console.log(JSON.stringify(zoneExport, null, 2));

    // Also save to localStorage for quick access
    const exportKey = `zone_export_${cfg.zoneType}_${Date.now()}`;
    try {
      localStorage.setItem(exportKey, JSON.stringify(zoneExport));
      console.log(`Exported to localStorage: ${exportKey}`);
    } catch (e) {
      console.warn('Failed to save to localStorage:', e);
    }
  }

  private handleTopBarMouse(e: import('../components/UIComponent').MouseEvent, layout: ReturnType<typeof this.getLayout>): boolean {
    const { x, y, width, height } = layout.topBar;
    const buttonY = y + 6;
    const buttonH = height - 12;

    if (e.type === 'down' && e.button === 'left') {
      // Save button
      const saveX = x + width - 85;
      if (e.x >= saveX && e.x <= saveX + 65 && e.y >= buttonY && e.y <= buttonY + buttonH) {
        this.saveCurrentMap();
        return true;
      }

      // New button
      const newX = saveX - 55;
      if (e.x >= newX && e.x <= newX + 50 && e.y >= buttonY && e.y <= buttonY + buttonH) {
        this.mapData = this.createEmptyMap(40, 30);
        this.currentMapId = null;
        this.currentMapName = 'Yeni Harita';
        this.mapOffsetX = 0;
        this.mapOffsetY = 0;
        this.notifyMapChange();
        return true;
      }

      // Load button
      const loadX = newX - 55;
      if (e.x >= loadX && e.x <= loadX + 50 && e.y >= buttonY && e.y <= buttonY + buttonH) {
        this.openLoadDialog();
        return true;
      }
    }

    return false;
  }

  private notifyMapChange(): void {
    if (this.onMapDataChange) {
      this.onMapDataChange(this.mapData);
    }
  }

  // Public API

  /**
   * Set the current map data for editing
   */
  setMapData(data: EditorMapData): void {
    this.mapData = data;
  }

  /**
   * Get the current map data
   */
  getMapData(): EditorMapData {
    return this.mapData;
  }

  /**
   * Create new empty map with specified dimensions
   */
  newMap(width: number, height: number): void {
    this.mapData = this.createEmptyMap(width, height);
    this.mapOffsetX = 0;
    this.mapOffsetY = 0;
    this.mapZoom = 0.5;
    this.notifyMapChange();
  }

  /**
   * Expand map in a direction with VOID tiles
   */
  expandMap(direction: 'north' | 'south' | 'east' | 'west', amount: number): void {
    const oldTiles = this.mapData.tiles;
    const oldWidth = this.mapData.width;
    const oldHeight = this.mapData.height;

    let newWidth = oldWidth;
    let newHeight = oldHeight;
    let offsetX = 0;
    let offsetY = 0;

    // Calculate new dimensions and offset
    switch (direction) {
      case 'north':
        newHeight += amount;
        offsetY = amount;
        break;
      case 'south':
        newHeight += amount;
        break;
      case 'west':
        newWidth += amount;
        offsetX = amount;
        break;
      case 'east':
        newWidth += amount;
        break;
    }

    // Create new tiles array filled with VOID
    const newTiles: number[][] = [];
    for (let y = 0; y < newHeight; y++) {
      newTiles[y] = [];
      for (let x = 0; x < newWidth; x++) {
        newTiles[y][x] = TileType.VOID;
      }
    }

    // Copy old tiles to new position
    for (let y = 0; y < oldHeight; y++) {
      for (let x = 0; x < oldWidth; x++) {
        const newX = x + offsetX;
        const newY = y + offsetY;
        if (newX >= 0 && newX < newWidth && newY >= 0 && newY < newHeight) {
          newTiles[newY][newX] = oldTiles[y][x];
        }
      }
    }

    // Update props positions
    for (const prop of this.mapData.props) {
      prop.x += offsetX;
      prop.y += offsetY;
    }

    // Update spawn areas positions
    for (const spawn of this.mapData.spawnAreas) {
      spawn.x += offsetX;
      spawn.y += offsetY;
    }

    // Update waypoints positions
    for (const wp of this.mapData.waypoints) {
      wp.x += offsetX;
      wp.y += offsetY;
    }

    // Update portals positions
    for (const portal of this.mapData.portals) {
      portal.x += offsetX;
      portal.y += offsetY;
    }

    // Apply changes
    this.mapData.width = newWidth;
    this.mapData.height = newHeight;
    this.mapData.tiles = newTiles;

    // Adjust map view offset to keep view centered on old content
    if (direction === 'west') {
      this.mapOffsetX -= amount * 16 * this.mapZoom;
    }
    if (direction === 'north') {
      this.mapOffsetY -= amount * 8 * this.mapZoom;
    }

    this.notifyMapChange();
    console.log(`[EditorTab] Map expanded ${direction} by ${amount} tiles. New size: ${newWidth}x${newHeight}`);
  }

  /**
   * Fill entire map with selected tile type
   */
  fillAllTiles(tileType: TileType): void {
    for (let y = 0; y < this.mapData.height; y++) {
      for (let x = 0; x < this.mapData.width; x++) {
        this.mapData.tiles[y][x] = tileType;
      }
    }
    this.notifyMapChange();
  }

  // ============== SAVE/LOAD FUNCTIONALITY ==============

  private currentMapId: string | null = null;
  private currentMapName: string = 'Yeni Harita';
  private showLoadDialog: boolean = false;
  private savedMapsList: MapListInfo[] = [];
  private loadDialogScrollY: number = 0;
  private hoveredMapIndex: number = -1;

  /**
   * Convert EditorMapData to SavedMapData format
   */
  private toSavedFormat(mapData: EditorMapData, name: string): SavedMapData {
    const saveManager = getSaveManager();
    return {
      id: this.currentMapId || saveManager.generateMapId(),
      name,
      timestamp: Date.now(),
      width: mapData.width,
      height: mapData.height,
      biome: 'forest', // Default biome
      tiles: mapData.tiles,
      props: mapData.props.map(p => ({
        type: p.type,
        x: p.x,
        y: p.y,
        variant: p.variant,
      })),
      spawns: mapData.spawnAreas.map(s => ({
        type: s.type,
        x: s.x,
        y: s.y,
        difficulty: s.difficulty,
      })),
    };
  }

  /**
   * Convert SavedMapData to EditorMapData format
   */
  private fromSavedFormat(savedData: SavedMapData): EditorMapData {
    return {
      width: savedData.width,
      height: savedData.height,
      tiles: savedData.tiles,
      props: savedData.props.map(p => ({
        type: p.type as PropType,
        x: p.x,
        y: p.y,
        variant: p.variant,
      })),
      spawnAreas: savedData.spawns.map(s => ({
        type: s.type as SpawnAreaType,
        x: s.x,
        y: s.y,
        radius: 3, // Default radius
        difficulty: s.difficulty,
      })),
      waypoints: [],
      portals: [],
      npcs: [],
    };
  }

  /**
   * Save current map to storage
   */
  saveCurrentMap(name?: string): boolean {
    const mapName = name || this.currentMapName;
    const savedData = this.toSavedFormat(this.mapData, mapName);

    const saveManager = getSaveManager();
    if (saveManager.saveMap(savedData)) {
      this.currentMapId = savedData.id;
      this.currentMapName = mapName;
      console.log(`[EditorTab] Map saved: ${mapName}`);
      return true;
    }
    return false;
  }

  /**
   * Load a map from storage by ID
   */
  loadMapById(mapId: string): boolean {
    const saveManager = getSaveManager();
    const savedData = saveManager.loadMap(mapId);

    if (savedData) {
      this.mapData = this.fromSavedFormat(savedData);
      this.currentMapId = savedData.id;
      this.currentMapName = savedData.name;
      this.mapOffsetX = 0;
      this.mapOffsetY = 0;
      this.mapZoom = 0.5;
      this.notifyMapChange();
      console.log(`[EditorTab] Map loaded: ${savedData.name}`);
      return true;
    }
    return false;
  }

  /**
   * Get list of saved maps
   */
  getSavedMaps(): MapListInfo[] {
    return getSaveManager().getMapList();
  }

  /**
   * Delete a map from storage
   */
  deleteMapById(mapId: string): boolean {
    return getSaveManager().deleteMap(mapId);
  }

  /**
   * Export current map as JSON string
   */
  exportMapAsJson(): string {
    const savedData = this.toSavedFormat(this.mapData, this.currentMapName);
    return JSON.stringify(savedData, null, 2);
  }

  /**
   * Import map from JSON string
   */
  importMapFromJson(jsonString: string): boolean {
    const saveManager = getSaveManager();
    const importedData = saveManager.importMap(jsonString);

    if (importedData) {
      this.mapData = this.fromSavedFormat(importedData);
      this.currentMapId = importedData.id;
      this.currentMapName = importedData.name;
      this.notifyMapChange();
      return true;
    }
    return false;
  }

  /**
   * Open load dialog
   */
  openLoadDialog(): void {
    this.savedMapsList = this.getSavedMaps();
    this.showLoadDialog = true;
    this.loadDialogScrollY = 0;
    this.hoveredMapIndex = -1;
  }

  /**
   * Close load dialog
   */
  closeLoadDialog(): void {
    this.showLoadDialog = false;
  }

  /**
   * Get current map name
   */
  getCurrentMapName(): string {
    return this.currentMapName;
  }

  /**
   * Set current map name
   */
  setCurrentMapName(name: string): void {
    this.currentMapName = name;
  }
}
